import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { delay } from 'lodash';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import {
  RegisterBanksSelect,
  mapTransactionTypeToBankType,
} from '@/modules/common/components/RegisterBanksSelect.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { useQuantityPricePerShareTotal } from '@/modules/common/hooks/useQuantityPricePerShareTotal.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCreateTransactionMutation } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { dividendReinvestmentOptions } from '@/modules/issuer-dashboard/components/modals/IssuerAddSubscriptionModal.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectButtons,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import {
  CreateTransactionRequest,
  RegisterBankExtended,
  ShareholderExtendResponse,
  TransactionStatus,
  TransactionType,
} from 'api';

const validationSchema = yup.object({
  shareholder_id: yup
    .number()
    .typeError('Shareholder id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  document_data: yup.array().of(yup.string().trim()),
  register_id: yup
    .number()
    .typeError('Register id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  register_class_id: yup
    .number()
    .typeError('Security class id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .moreThan(0, 'Quantity must be more than 0')
    .required('This field is required'),
  price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be more than 0')
    .required('This field is required'),
  to_bank_id: yup.number().moreThan(0, 'This field is required').required('This field is required'),
  memo: yup.string().trim(),
  total: yup
    .number()
    .typeError('Total must be a number')
    .moreThan(0, 'Total must be more than 0')
    .required('This field is required'),
  issue_date: yup.string().trim().required('This field is required'),
  dividend_reinvestment: yup.boolean().when(['type'], ([type], schema) => {
    return type === TransactionType.Subscription || type === TransactionType.OpeningBalance
      ? schema.required('This select is required')
      : schema.nullable();
  }),
  status: yup
    .mixed<TransactionStatus>()
    .oneOf(Object.values(TransactionStatus))
    .required('This select is required'),
  payment_issue_date: yup
    .string()
    .trim()
    .when('status', ([status], schema) => {
      if (status === TransactionStatus.Completed) {
        return schema.required('This field is required');
      }
      return schema.nullable();
    }),
  do_not_notify_investor: yup.boolean().required('This select is required'),
});

const initialValues: CreateTransactionRequest & { register_id: number } = {
  register_class_id: 0,
  shareholder_id: 0,
  type: TransactionType.OpeningBalance,
  quantity: 1,
  price_per_share: 0,
  memo: '',
  dividend_reinvestment: false,
  dividend_event_id: undefined,
  total: 0,
  register_id: 0,
  issue_date: '',
  document_data: [],
  to_bank_id: undefined,
  status: TransactionStatus.Pending,
  payment_issue_date: undefined,
  do_not_notify_investor: false,
};

interface RegisterClassSelectPaginate {
  id: number;
  price_per_share: number;
}

export interface IssuerAddOpeningBalanceModalProps extends BaseModalProps {
  register_id: number;
  mode: TransactionType;
  shareholderId?: number;
}

export const IssuerAddOpeningBalanceModal = ({
  open,
  register_id,
  mode,
  onClose,
  shareholderId,
}: IssuerAddOpeningBalanceModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const formik = useFormik({
    initialValues: {
      ...initialValues,
      register_id,
      type: mode,
      shareholder_id: shareholderId ?? initialValues.shareholder_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await createSubscription(values);
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { data: registerData } = useRegisterQuery(
    formik.values.register_id,
    !formik.values.register_id,
  );

  const { rounding_type, allows_fractional_shares = true } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const { onChangeTotal, onChangeQuantity, onChangePricePerShare } = useQuantityPricePerShareTotal({
    formik,
    allowsFractionalSharesQuantity: allows_fractional_shares,
    roundingTypeQuantity: rounding_type,
  });

  const { mutateAsync: createSubscription } = useCreateTransactionMutation({
    setErrors: formik.setErrors,
  });

  const { data: registersResponse, isLoading: isRegistersLoading } = useCurrentUserRegistersQuery();
  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: formik.values.register_id },
    true,
  );
  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: formik.values.register_id });

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return registersResponse.data?.map(item => ({
      label: item.register_obj?.name ?? '',
      value: item.register_id,
    }));
  }, [registersResponse]);

  const registerShareholderListOptions = useMemo<SelectOption<ShareholderExtendResponse>[]>(() => {
    if (!registerShareholderList?.shareholders) return [];

    return registerShareholderList?.shareholders.map(item => ({
      label: item.entity.entity_name,
      value: { ...item, toString: () => item.id },
    }));
  }, [registerShareholderList]);

  const handleAsyncRegisterClassList = async (currentPage: number, search?: string) => {
    const response = await fetchNextRegisterClassListPage({
      pageParam: {
        page: currentPage,
        search,
      },
    });

    const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
      response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id, current_price_per_share }) => ({
          label: class_name,
          value: { id, price_per_share: current_price_per_share, toString: () => class_name },
        }));
      }, []) || [];
    return options;
  };

  const handleChangeRegister = (registerId: number | null) => {
    formik.setFieldValue('register_id', registerId);
    formik.setFieldValue('quantity', initialValues.quantity);
  };

  const handleChangeShareholder = useCallback(
    (shareholder: ShareholderExtendResponse | null) => {
      formik.setFieldValue('shareholder_id', shareholder?.id);
      formik.setFieldError('shareholder_id', '');

      const reinvestmentElections = shareholder?.dividend_reinvestment_extended;
      if (formik.values.register_class_id && reinvestmentElections) {
        const currentReinvestmentElection = reinvestmentElections.find(
          item => item.id === formik.values.register_class_id,
        )?.is_reinvest;
        formik.setFieldValue('dividend_reinvestment', currentReinvestmentElection);
        formik.setFieldError('dividend_reinvestment', '');
      }
    },
    [formik.values.register_class_id],
  );

  const handleChangeRegisterClass = useCallback(
    (register_class_id: number | null, price_per_share: number | null) => {
      formik.setFieldValue('register_class_id', register_class_id);
      formik.setFieldValue('price_per_share', price_per_share);
      formik.setFieldValue('quantity', initialValues.quantity);
      formik.setFieldValue(
        'total',
        price_per_share ? initialValues.quantity * price_per_share : null,
      );
      formik.setFieldError('register_class_id', '');
      formik.setFieldError('price_per_share', '');
      formik.setFieldError('quantity', '');
      formik.setFieldError('total', '');
      formik.setFieldError('issue_date', '');

      if (formik.values.shareholder_id && registerShareholderListOptions && register_class_id) {
        const reinvestmentElections = registerShareholderListOptions?.find(
          item => item.value.id === formik.values.shareholder_id,
        )?.value?.dividend_reinvestment_extended;
        const currentReinvestmentElection = reinvestmentElections?.find(
          item => item.id === register_class_id,
        )?.is_reinvest;
        formik.setFieldValue('dividend_reinvestment', currentReinvestmentElection);
        formik.setFieldError('dividend_reinvestment', '');
      }
    },
    [formik.values.shareholder_id, registerShareholderListOptions],
  );

  const handleChangeDocuments = (filesData: string[]) => {
    formik.setFieldValue('document_data', filesData);
    formik.setFieldError('document_data', '');
  };

  const handleChangeReinvestment = (reinvestment: boolean) => {
    formik.setFieldValue('dividend_reinvestment', reinvestment);
    formik.setFieldError('dividend_reinvestment', '');
  };

  const handleChangeSelect =
    (field: keyof CreateTransactionRequest) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    };

  const handleChangeDate =
    (fieldName: keyof CreateTransactionRequest): CalendarInputProps['onChange'] =>
    date => {
      if (!date) {
        formik.setFieldValue(fieldName, '');
        formik.setFieldError(fieldName, '');
        return;
      }
      const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');
      formik.setFieldValue(fieldName, currentDate);
      formik.setFieldError(fieldName, '');
    };

  const handleChangeBank =
    (fieldName: keyof CreateTransactionRequest) =>
    (item: SelectPaginateOption<RegisterBankExtended> | null) => {
      formik.setFieldValue(fieldName, item?.value.id);
      formik.setFieldError(fieldName, '');
    };

  return (
    <ModalContentWrapper open={open} title="Add Opening Balance" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <div className="w-full flex items-start space-x-6">
              <Select
                label="REGISTER"
                options={registerOptions}
                value={formik.values.register_id}
                isLoading={isRegistersLoading}
                onChange={handleChangeRegister}
                error={formik.errors.register_id}
              />
              <SelectPaginate<RegisterClassSelectPaginate>
                key={formik.values.register_id}
                onAsync={handleAsyncRegisterClassList}
                label="CLASS"
                onChange={item => {
                  formik.setFieldValue('to_bank_id', null);
                  delay(
                    handleChangeRegisterClass,
                    1,
                    item?.value?.id || null,
                    item?.value?.price_per_share || null,
                  );
                }}
                error={formik.errors.register_class_id}
                isClearable={true}
              />
            </div>
            {!!formik.values.register_class_id && !!formik.values.register_id && (
              <RegisterBanksSelect
                onChange={handleChangeBank('to_bank_id')}
                value={formik.values.to_bank_id}
                label="To Bank"
                key={`To_Bank_direction_${formik.values.type}`}
                clearable={true}
                error={formik.errors.to_bank_id}
                needBankType={mapTransactionTypeToBankType[formik.values.type]}
                prefillBank={true}
                is_overall={true}
              />
            )}
            <Select<ShareholderExtendResponse>
              label="SHAREHOLDER"
              options={registerShareholderListOptions}
              isLoading={isRegisterShareholderListLoading}
              value={{ id: formik.values.shareholder_id } as ShareholderExtendResponse}
              onChange={handleChangeShareholder}
              error={formik.errors.shareholder_id}
            />
            {mode === TransactionType.OpeningBalance && (
              <CalendarInput
                label="Units issue date"
                name="issue_date"
                onChange={handleChangeDate('issue_date')}
                error={formik.errors.issue_date}
                format="DD-MM-YYYY HH:mm"
                value={formik.values.issue_date ? dayjs(formik.values.issue_date) : undefined}
                showTime={true}
              />
            )}
            <CurrencyInput
              currency=""
              name="quantity"
              label="quantity of shares"
              value={formik.values.quantity}
              allowFractional={allows_fractional_shares}
              roundingType={rounding_type}
              onChange={onChangeQuantity}
              disabled={!formik.values.register_class_id}
            />
            <CurrencyInput
              name="total"
              label="total"
              error={formik.errors.total}
              value={formik.values.total}
              onChange={onChangeTotal}
            />
            <CurrencyInput
              name="price_per_share"
              label={`price per share/unit (${getRegisterTypeStrings('nav', {
                registerId: formik.values.register_id,
              })})`}
              decimalPoints={4}
              value={formik.values.price_per_share}
              error={formik.errors.price_per_share}
              onChange={onChangePricePerShare}
            />

            {/* <Select */}
            {/*   value={formik.values.status} */}
            {/*   label="Status" */}
            {/*   placeholder="Select transaction status" */}
            {/*   onChange={value => { */}
            {/*     handleChangeSelect('status')(value); */}
            {/*     if (value === TransactionStatus.Pending) { */}
            {/*       handleChangeSelect('payment_issue_date')(undefined); */}
            {/*     } */}
            {/*   }} */}
            {/*   options={getSelectOptionByEnum<TransactionStatus>(TransactionStatus).filter( */}
            {/*     item => */}
            {/*       item.value === TransactionStatus.Pending || */}
            {/*       item.value === TransactionStatus.Completed, */}
            {/*   )} */}
            {/*   error={formik.errors.status} */}
            {/* /> */}

            {formik.values.status === TransactionStatus.Completed && (
              <CalendarInput
                label="Payment date"
                name="payment_issue_date"
                onChange={handleChangeDate('payment_issue_date')}
                error={formik.errors.payment_issue_date}
                format="DD-MM-YYYY HH:mm"
                value={
                  formik.values.payment_issue_date
                    ? dayjs(formik.values.payment_issue_date)
                    : undefined
                }
                showTime={true}
              />
            )}

            <CustomCheckbox
              label="Do not notify investor"
              checked={formik.values.do_not_notify_investor}
              onChange={e => handleChangeSelect('do_not_notify_investor')(e.target.checked)}
            />

            <SelectButtons<boolean>
              label="REINVESTMENT DISTRIBUTION ELECTION"
              options={dividendReinvestmentOptions}
              selected={dividendReinvestmentOptions.find(
                ({ value }) => value === formik.values.dividend_reinvestment,
              )}
              onChange={handleChangeReinvestment}
              error={formik.errors.dividend_reinvestment}
              className="mt-6"
            />
            <UploadDocumentsBlock
              label="Upload document JPG, PNG or PDF. Up to 10 mb (Optional)"
              error={formik.errors.document_data}
              onChange={handleChangeDocuments}
              value={formik.values.document_data || []}
            />
            <TextInputField
              name="memo"
              label="Memo (Optional)"
              placeholder="Memo..."
              className="resize-none"
            />
          </div>
          <Button onClick={formik.submitForm} loading={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
